import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { DropdownObject } from '@inmoment/the-kitchen';

import { ChartOptionConstants, Constants } from '../../../../../app/constants/constants';
import { ChartDisplayOptions } from '../../../../models';
import {
  MetricBoxType,
  MetricEnum,
  MetricNumberType,
  WidgetChartOptions,
} from '../widget-chart/widget-chart.component';

@Component({
  selector: 'app-gauge-chart-options-dialog',
  templateUrl: './gauge-chart-options-dialog.component.html',
  styleUrls: ['./gauge-chart-options-dialog.component.scss'],
  standalone: false,
})
export class GaugeChartOptionsDialogComponent implements OnInit, OnDestroy {
  @Input() public data: ChartDisplayOptions;

  @Output() modalClosed: EventEmitter<ChartDisplayOptions> =
    new EventEmitter<ChartDisplayOptions>();

  readonly boxTypeSet: DropdownObject[] = [
    { title: translate('widgets.options.top'), id: 'top' },
    { title: translate('widgets.options.bottom'), id: 'bottom' },
  ];
  readonly comparisonColorRadios = [
    {
      label: translate('widgets.options.colors.gray'),
      value: 'gray',
    },
    {
      label: translate('widgets.options.colors.red'),
      value: 'red',
    },
    {
      label: translate('widgets.options.colors.green'),
      value: 'green',
    },
  ];

  axisValues: DropdownObject[] = [
    {
      id: 'default',
      title: translate('widgets.options.default'),
    },
    {
      id: 'custom',
      title: translate('widgets.options.custom'),
    },
  ];

  form: FormGroup;
  decimalPrecisionSet: DropdownObject[];
  errorMessages: Map<string, string> = new Map();
  boxSizeSet: DropdownObject[];

  min: number;
  max: number;
  errorMessage: string;

  private unsub$: Subject<void> = new Subject<void>();

  constructor(formBuilder: FormBuilder) {
    this.configForm(formBuilder);

    this.boxSizeSet = ChartOptionConstants.TOPBOXSIZEDOS;
    this.decimalPrecisionSet = ChartOptionConstants.DECIMALPRECISIONDOS;
  }

  ngOnInit(): void {
    this.loadForm();

    this.form.controls.metric.valueChanges.pipe(takeUntil(this.unsub$)).subscribe((metric) => {
      this.resetAxisValues(metric);
    });

    this.form.controls.axisValues.valueChanges.pipe(takeUntil(this.unsub$)).subscribe((id) => {
      const skipMinMaxValues = id === 'custom';
      this.updateValueAndValidity(null, null, null, skipMinMaxValues);
    });

    this.minControl.valueChanges.pipe(takeUntil(this.unsub$)).subscribe(() => {
      this.setErrorStatusForMinMax(true);
    });

    this.maxControl.valueChanges.pipe(takeUntil(this.unsub$)).subscribe(() => {
      this.setErrorStatusForMinMax(false);
    });
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  onSave(): void {
    const result = this.createResult();
    this.modalClosed.emit(result);
  }

  onCancel(): void {
    this.modalClosed.emit();
  }

  onMinMaxValueChange(value: string, isMin: boolean): void {
    if (value === '') {
      const control = isMin ? this.minControl : this.maxControl;
      control.setValue(null);
      control.setErrors(null);
      this.errorMessage = undefined;
    }
  }

  updateValueAndValidity(
    metric?: MetricEnum,
    savedMin?: number,
    savedMax?: number,
    skipMinAndMaxValidators = false
  ) {
    if (this.form) {
      const { min, max } = this.getMinMax(metric ?? this.form.controls.metric.value);
      this.min = savedMin ?? min;
      this.max = savedMax ?? max;

      this.setValidators(this.minControl, min, max, true, skipMinAndMaxValidators);
      this.setValidators(this.maxControl, min, max, false, skipMinAndMaxValidators);
    }
  }

  get minControl() {
    return this.form.controls.min;
  }

  get maxControl() {
    return this.form.controls.max;
  }

  private configForm(formBuilder: FormBuilder): void {
    const metric = MetricEnum.AVG;
    const { min, max } = this.getMinMax(metric);
    this.min = min;
    this.max = max;
    this.form = formBuilder.group({
      metric: new FormControl(metric, Validators.required),
      metricBoxType: new FormControl(MetricBoxType.TOP, Validators.required),
      metricBoxSize: new FormControl('1', Validators.required),
      minRating: new FormControl(0, Validators.required),
      maxRating: new FormControl(10, [
        Validators.required,
        Validators.min(2),
        Validators.max(1000),
      ]),
      decimalPrecision: new FormControl('2', Validators.required),
      increaseColor: new FormControl('gray', Validators.required),
      decreaseColor: new FormControl('gray', Validators.required),
      showAs: new FormControl(MetricNumberType.ABSOLUTE, Validators.required),
      axisValues: new FormControl('default', Validators.required),
      min: new FormControl(
        this.min,
        Validators.compose([this.hasDecimals, Validators.min(this.min), Validators.max(this.max)])
      ),
      max: new FormControl(
        this.max,
        Validators.compose([this.hasDecimals, Validators.min(this.min), Validators.max(this.max)])
      ),
    });
    this.form.controls.metric.valueChanges.pipe(takeUntil(this.unsub$)).subscribe((v) => {
      if (v === MetricEnum.BOX) {
        this.form.controls.metricBoxType.enable({ onlySelf: true, emitEvent: false });
        this.form.controls.metricBoxSize.enable({ onlySelf: true, emitEvent: false });
      } else {
        this.form.controls.metricBoxType.disable({ onlySelf: true, emitEvent: false });
        this.form.controls.metricBoxSize.disable({ onlySelf: true, emitEvent: false });
      }
    });
    this.form.controls.metricBoxType.valueChanges.pipe(takeUntil(this.unsub$)).subscribe((v) => {
      if (v === MetricBoxType.BOTTOM) {
        this.boxSizeSet = ChartOptionConstants.BOTTOMBOXSIZEDOS;
      } else {
        this.boxSizeSet = ChartOptionConstants.TOPBOXSIZEDOS;
      }
      this.form.controls.metricBoxSize.setValue(Constants.TWTRA_DEFAULT_BOX_SIZE);
    });
  }

  private loadForm(): void {
    const gaugeOptions = (this.data.payload as WidgetChartOptions).gaugeOptions;

    this.form.patchValue({
      metric: gaugeOptions.metric,
      minRating: gaugeOptions.minRating,
      maxRating: gaugeOptions.maxRating,
      decimalPrecision: (
        gaugeOptions.decimalPrecision ?? ChartOptionConstants.DEFAULT_DECIMAL_PRECISION
      ).toString(),
      increaseColor: gaugeOptions.increaseColor,
      decreaseColor: gaugeOptions.decreaseColor,
      showAs: gaugeOptions.showAs,
      axisValues: gaugeOptions.axisValues ?? 'default',
      min: gaugeOptions.min,
      max: gaugeOptions.max,
    });
    if (gaugeOptions.metric === MetricEnum.BOX) {
      this.form.patchValue({
        metricBoxType: gaugeOptions.metricBoxType,
        metricBoxSize: gaugeOptions.metricBoxSize,
      });
    }
    const skipMinAndMaxValidators = gaugeOptions.axisValues === 'custom';
    this.updateValueAndValidity(
      gaugeOptions.metric,
      gaugeOptions.min,
      gaugeOptions.max,
      skipMinAndMaxValidators
    );
  }

  private createResult(): ChartDisplayOptions {
    this.resetValuesIfMinOrMaxIsEmpty();

    const result = { ...this.data };
    result.needRefresh = true;
    result.payload.gaugeOptions = {
      metric: this.form.controls.metric.value,
      minRating: this.form.controls.minRating.value,
      maxRating: this.form.controls.maxRating.value,
      decimalPrecision: parseInt(this.form.controls.decimalPrecision.value, 10),
      increaseColor: this.form.controls.increaseColor.value,
      decreaseColor: this.form.controls.decreaseColor.value,
      showAs: this.form.controls.showAs.value,
      axisValues: this.form.controls.axisValues.value,
      min: this.minControl.value,
      max: this.maxControl.value,
    };
    if (result.payload.gaugeOptions.metric === MetricEnum.BOX) {
      result.payload.gaugeOptions.metricBoxType = this.form.controls.metricBoxType.value;
      result.payload.gaugeOptions.metricBoxSize = this.form.controls.metricBoxSize.value;
    }

    return result;
  }

  private resetAxisValues(metric?: MetricEnum): void {
    const gaugeMetric = metric ?? MetricEnum.AVG;

    if (this.form) {
      this.form.controls.axisValues.setValue('default');
      this.updateValueAndValidity(gaugeMetric);
    }
  }

  private setErrorStatusForMinMax(isMin: boolean): void {
    const control = isMin ? this.minControl : this.maxControl;

    if (control.errors === null) {
      this.errorMessage = undefined;
    } else if (control.errors['hasDecimals']) {
      this.errorMessage = translate('widgets.options.error-decimals-not-allowed');
    }

    if (this.minControl.value > this.maxControl.value) {
      control.setErrors({ errorMinMax: true });
      this.errorMessage = translate('widgets.options.error-min-higher-max');
    }

    if (this.minControl.value === this.maxControl.value) {
      control.setErrors({ errorEqual: true });
      this.errorMessage = translate('widgets.options.error-min-equal-max');
    }
    if (this.minControl.hasError('errorEqual') && this.minControl.value < this.maxControl.value) {
      this.minControl.setErrors(null);
      this.errorMessage = undefined;
    }
  }

  private hasDecimals(control: AbstractControl) {
    if (/\.\d+$/.test(control.value)) {
      return { hasDecimals: true };
    }

    return null;
  }

  private getMinMax(metric: MetricEnum) {
    switch (metric) {
      case MetricEnum.AVG:
        return { min: 0, max: 10 };
      case MetricEnum.NPS:
        return { min: -100, max: 100 };
      case MetricEnum.BOX:
        return { min: 0, max: 100 };
    }
  }

  private setValidators(
    control: AbstractControl,
    min: number,
    max: number,
    isMin: boolean,
    skipMinAndMaxValidators?: boolean
  ) {
    control.setValue(isMin ? this.min : this.max);
    control.setErrors(null);
    let validators = [];
    validators.push(this.hasDecimals);
    if (!skipMinAndMaxValidators) {
      validators = [...validators, Validators.min(min), Validators.max(max)];
    }
    control.setValidators(validators);
    control.updateValueAndValidity();
  }

  private resetValuesIfMinOrMaxIsEmpty(): void {
    if (this.minControl.value === null || this.maxControl.value === null) {
      this.resetAxisValues(this.form.controls.metric.value);
    }
  }
}
